import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { STORAGE_TOKEN, STORAGE_USER_INFO } from "constants/common/common";
import { CommonResponseType, ErrorStatusType } from "constants/types/common";
import { LOGIN } from "routes/route.constant";

let isRefreshing = false;
let failedQueue: any[] = [];

// Hàm để xử lý hàng đợi (Retry các request cũ hoặc hủy nếu refresh thất bại)
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  localStorage.removeItem(STORAGE_TOKEN);
  localStorage.removeItem(STORAGE_USER_INFO);
  window.dispatchEvent(new Event("auth:logout"));
};

const onRequest = (config: AxiosRequestConfig): AxiosRequestConfig | any => {
  const tokenString = localStorage.getItem(STORAGE_TOKEN);
  if (tokenString) {
    try {
      // 2. Parse chuỗi JSON để lấy lại object và trích xuất access_token
      const tokenData = JSON.parse(tokenString);
      const accessToken = tokenData.access_token;

      // 3. Nếu có accessToken, gắn vào headers
      if (accessToken && config.headers) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    } catch (error) {
      console.error("Lỗi parse dữ liệu token từ localStorage:", error);
    }
  }
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  console.error(`[request error] [${JSON.stringify(error)}]`);
  return Promise.reject(error);
};

const onResponse = (
  response: AxiosResponse,
): AxiosResponse<CommonResponseType<any, string>> => {
  return response;
};

const onResponseError = (error: AxiosError): Promise<AxiosError> | any => {
  const originalRequest = error.config as any;

  if (error.response) {
    const { status, data }: { status: number; data: any } = error.response;
    const errorData: any = data; // Chú ý: Thay bằng ErrorStatusType của bạn

    // 1. Xử lý riêng cho lỗi 401 và đảm bảo chưa từng retry request này
    if (status === 401 && !originalRequest._retry) {
      // Nếu đang trong quá trình refresh token, đưa request vào hàng đợi
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest); // Thực thi lại request bị lỗi
          })
          .catch((err) => Promise.reject(err));
      }

      // Đánh dấu là đang lấy token mới
      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise((resolve, reject) => {
        const tokenString = localStorage.getItem(STORAGE_TOKEN);
        const tokenData = tokenString ? JSON.parse(tokenString) : null;
        const refreshToken = tokenData?.refresh_token;
        const currentAccessToken = tokenData?.access_token;

        if (!refreshToken) {
          handleLogout();
          return reject(errorData);
        }

        // 2. Gọi API Refresh Token
        // Lưu ý: Dùng axios mặc định (không qua interceptor này) để tránh lặp vô tận
        axios
          .post(
            "/api/jwt/refresh",
            {
              // THAY ĐỔI ĐƯỜNG DẪN API TẠI ĐÂY
              refresh_token: refreshToken,
            },
            {
              // Tham số thứ 3: Config object chứa headers
              headers: {
                Authorization: `Bearer ${currentAccessToken}`,
              },
            },
          )
          .then(({ data: refreshResponse }) => {
            // Lấy token mới từ response (cấu trúc tùy BE của bạn trả về)
            const newAccessToken = refreshResponse.data.access_token;
            const newRefreshToken =
              refreshResponse.data.refresh_token || refreshToken;

            // Cập nhật lại localStorage
            localStorage.setItem(
              STORAGE_TOKEN,
              JSON.stringify({
                ...tokenData,
                access_token: newAccessToken,
                // Cập nhật cả refresh_token mới nếu BE có trả về
                refresh_token: newRefreshToken,
              }),
            );
            window.dispatchEvent(new Event("auth:token-refreshed"));

            // Gắn token mới vào request hiện tại
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            // Xử lý các request đang chờ trong queue
            processQueue(null, newAccessToken);

            // Thực thi lại request ban đầu với token mới
            resolve(axios(originalRequest));
          })
          .catch((err) => {
            // Nếu call refresh token thất bại (vd: refresh_token hết hạn/không hợp lệ)
            processQueue(err, null);
            handleLogout();
            reject(errorData); // Trả về format lỗi cũ của bạn
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    // 3. Xử lý các lỗi khác ngoài 401 (Giữ nguyên logic của bạn)
    return Promise.reject(errorData);
  }

  // Lỗi mạng hoặc lỗi không nhận được response
  return Promise.reject(error.response || error);
};

export function setupInterceptorsTo(
  axiosInstance: AxiosInstance,
): AxiosInstance {
  axiosInstance.interceptors.request.use(onRequest, onRequestError);
  axiosInstance.interceptors.response.use(onResponse, onResponseError);
  return axiosInstance;
}
